import {RefObject, useState} from 'react';
import {
  InViewHookResponse,
  IntersectionOptions,
} from 'react-intersection-observer';
import {useInViewEffect} from './useInViewEffect';

type SimpleInViewHookResponse<T> = {
  inView: InViewHookResponse['inView'];
  ref: RefObject<T>;
};

/**
 * React Hooks make it easy to monitor the `inView` state of your components. Call
 * the `useInView` hook with the (optional) [options](#options) you need. It will
 * return an array containing a `ref`, the `inView` status and the current
 * Assign the `ref` to the DOM element you want to monitor, and the hook will
 * report the status.
 * This is a modified version of 'react-intersection-observer' `useInView` hook that does not export entries and therefore saves 1 re-rendering in all cases where it is not required at all. Check https://github.com/thebuilder/react-intersection-observer/blob/master/src/useInView.tsx
 *
 * @example
 * ```jsx
 * import React from 'react';
 * import { useInView } from '@aboutyou/ui-components';
 *
 * const Component = () => {
 *   const { ref, inView } = useInView({
 *       threshold: 0,
 *   });
 *
 *   return (
 *     <div ref={ref}>
 *       <h2>{`Header inside viewport ${inView}.`}</h2>
 *     </div>
 *   );
 * };
 * ```
 */
export function useInView<T extends Element = Element>({
  threshold,
  delay,
  trackVisibility,
  rootMargin,
  root,
  triggerOnce,
  skip,
  initialInView,
}: IntersectionOptions = {}): SimpleInViewHookResponse<T> {
  const [inView, setInView] = useState<boolean>(!!initialInView);

  const {ref} = useInViewEffect<T>(setInView, {
    threshold,
    delay,
    trackVisibility,
    rootMargin,
    root,
    triggerOnce,
    skip,
    initialInView,
  });

  return {
    ref,
    inView,
  };
}
